import React, { useEffect, useState } from 'react';
import { QueryParamsStrategies, getSearchToolsQueryParams } from '@shared-utils/search-tools-query-params-mapper';
import { get } from 'lodash';
import { useLocalization } from '@shared-ui/localization-context';

//components
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { DatePicker } from 'src/components/common/modules/form';
import { PrimaryButton, PrimaryButtonProps } from 'src/components/common/ui/button/button';

//types
import { UitkButtonSize } from 'uitk-react-button';

//helpers
import {
  getLocalizationData,
  getDateFormatWithLang,
  WizardDefaultProps,
  getWizardDefaults,
  getWizardsOverrides,
  wizardDate,
  getValidReturnDate,
  linkTabOpenner,
  renderInvalidTypeheadStyles,
} from '../../helpers';
import { checkValue } from 'src/components/common/helper';
import gaLogger from 'src/utils/ga-logger';
import { useStores } from 'src/utils/useStore';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkSubheading } from 'uitk-react-text';
import { SelectDropdown } from 'src/components/common/modules/form/selectDropdown/selectDropdown';
import { CruiseTraveller } from 'src/components/common/modules/form/cruiseTraveller/cruiseTraveller';
import { CRUISE_ADVISOR_CONTACT } from './data';
import { sendSearchWizardSearchGTMEvent } from 'src/components/common/utils/gtmHelperFunctions';

export const Cruise: React.FC<any> = ({ localizationData, device, data, mccidData }) => {
  const { context } = useStores();

  const { formatDateString } = useLocalization();

  const searchdDateFormat = getDateFormatWithLang(context?.locale, 'yyyy-MM-dd');
  const today = new Date();
  const dayAfterOneMonth = new Date();
  const nextDayAfterDayAfterOneMonth = new Date();
  dayAfterOneMonth.setDate(today.getDate() + 29);
  nextDayAfterDayAfterOneMonth.setDate(today.getDate() + 30);

  // Check the default data is coming from the searchOverride if so
  // Get the code
  const wizardDefault: WizardDefaultProps = {
    data,
    lob: 'Cruises',
    overrideController: 'returnDefault',
  };
  const wizardOverrideDefault = getWizardDefaults(wizardDefault);
  let returnDefault = '';
  if (checkValue(wizardOverrideDefault, true)) {
    const getReturn = wizardOverrideDefault?.find((w) => w.key === 'returnDefault');
    if (checkValue(getReturn) && checkValue(getReturn.value)) {
      returnDefault = getReturn?.value;
    }
  } else {
    returnDefault = get(data, 'returnDefault', '');
  }

  // Get Wizard Overrides Date
  const wizardOverrides = getWizardsOverrides(data, 'Cruises');
  let wizardOverridesDepartureDate = '';
  let wizardOverridesReturnDate = '';
  if (checkValue(wizardOverrides, true)) {
    wizardOverridesDepartureDate = wizardOverrides?.[0]?.dateFrom;
    wizardOverridesReturnDate = wizardOverrides?.[0]?.dateTo;
  }
  const departureDate = wizardDate(
    get(data, 'departureDate', ''),
    get(data, 'departureDateFixed', ''),
    wizardOverridesDepartureDate,
    today,
    get(data, 'daysInFuture', ''),
    ''
  );
  const returnDatewizardDate = wizardDate(
    get(data, 'returnDate', ''),
    get(data, 'returnDateFixed', ''),
    wizardOverridesReturnDate,
    nextDayAfterDayAfterOneMonth,
    get(data, 'daysInFuture', ''),
    get(data, 'lengthOfStay', '')
  );
  const returnDate = getValidReturnDate(departureDate, returnDatewizardDate);

  //init state
  const initFormState = {
    destination: { value: returnDefault, error: '', dropDownId: 'cruiseDestinationWizard' },
    startDate: {
      value: departureDate,
      error: '',
    },
    endDate: {
      value: returnDate,
      error: '',
    },
  };

  //formstate handle
  const [formState, setFormState] = useState(initFormState);

  //onChangeHandler
  const onChangeHandler = (state, value) => {
    setFormState((prev) => ({
      ...prev,
      [state]: {
        ...prev?.[state],
        value,
        error: '',
      },
    }));
  };

  //handle validations
  const handleValidations = () => {
    let isValidate = true;
    let destinationError = '';

    if (formState?.destination?.value === '') {
      isValidate = false;
      destinationError = getLocalizationData(
        localizationData,
        'select_origin_or_airport',
        'Please select a destination',
        device
      );
    }

    setFormState((prev) => ({
      ...prev,
      destination: { ...prev?.destination, error: destinationError },
    }));

    return isValidate;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    if (handleValidations()) {
      const tempobj = { adults: '1' };
      const { elements } = event.currentTarget;
      const inputs = Array.from(elements) as HTMLInputElement[];

      const queryParams = getSearchToolsQueryParams(QueryParamsStrategies.CRUISES, inputs);
      let searchURL = `/Cruise-Search${queryParams}`;
      inputs.forEach((input) => {
        const inputVal: string = checkValue(input.value) ? input.value : '';
        if (input.name !== '') {
          if (input.name === 'traveler-adults') {
            tempobj.adults = inputVal;
          }
          return { ...tempobj };
        }
      });
      searchURL +=
        '&adultsCount=' + 
        tempobj?.adults +
        '&destination=' +
        formState?.destination?.value +
        '&d1=' +
        formatDateString(formState?.startDate?.value, { raw: 'yyyy-MM-dd' }) +
        '&d2=' +
        formatDateString(formState?.endDate?.value, { raw: 'yyyy-MM-dd' }) +
        '&cruise-line=';

      const searchParameters =
        formatDateString(formState?.startDate?.value, { skeleton: 'MMMd' }) +
        '-' +
        formatDateString(formState?.endDate?.value, { skeleton: 'MMMd' }) +
        ' ' +
        formState?.destination?.value;

      gaLogger.logGa('wizard search', 'cruises tab', searchParameters);
      
      sendSearchWizardSearchGTMEvent('wizard_search', 'Cruise', searchParameters , searchURL);

      linkTabOpenner(searchURL, mccidData);
    }
  };

  //search button props
  const primaryButtonProps: PrimaryButtonProps = {
    text: getLocalizationData(localizationData, 'search_button_copy', 'Search', device),
    tag: 'button',
    gaTracking: undefined,
    exClass: `btn mobile-btn`,
    target: '_blank',
    size: UitkButtonSize.LARGE,
  };

  //typehead error state styles add
  useEffect(() => {
    const errors = Object.keys(formState)
      .map((key) => ({
        error: get(formState, `[${key}].error`, ''),
        typeheadId: get(formState, `[${key}].dropDownId`, ''),
      }))
      .filter(({ typeheadId }) => typeheadId !== '' && typeheadId !== false && typeheadId !== undefined);

    renderInvalidTypeheadStyles(errors);
  }, [formState]);

  return (
    <>
      <div className="activity-wrapper" data-testid="activity-search-form">
        <form onSubmit={handleSubmit}>
          <UitkSpacing margin={{ block: 'half' }} padding={{ block: 'two' }}>
            <UitkLayoutFlex space="six" alignItems="center" justifyContent="space-between">
              <UitkLayoutFlexItem>
                <UitkSubheading tag="p">For expert cruise advice, call {CRUISE_ADVISOR_CONTACT}.</UitkSubheading>
              </UitkLayoutFlexItem>
              <UitkLayoutFlexItem>
                <CruiseTraveller />
              </UitkLayoutFlexItem>
            </UitkLayoutFlex>
          </UitkSpacing>
          <UitkLayoutGrid
            columns={{ small: 1, medium: ['1fr', '2fr'] }}
            space="three"
            className="wizard-row-top-margin"
          >
            <UitkLayoutGridItem>
              <div>
                <SelectDropdown
                  id={formState?.destination?.dropDownId}
                  label={getLocalizationData(localizationData, 'activities_in_label_copy', 'Going to', device)}
                  name={'EGDSSearchFormLocationField-Location-destination_form_field'}
                  gaTracking={{
                    eventCategory: '',
                    eventAction: '',
                    eventLabel: '',
                  }}
                  onChangeEvent={(e) => onChangeHandler('destination', e?.target?.value)}
                  error={formState?.destination?.error}
                />
              </div>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem>
              <div>
                <DatePicker
                  updateDates={(startDate, endDate, startDateState, endDateState) => {
                    onChangeHandler(startDateState, formatDateString(startDate, { raw: 'yyyy-MM-dd' }));
                    onChangeHandler(endDateState, formatDateString(endDate, { raw: 'yyyy-MM-dd' }));
                  }}
                  startDate={formState?.startDate?.value}
                  endDate={formState?.endDate?.value}
                  startPlaceHolder={getLocalizationData(localizationData, 'from_date_label_copy', 'From', device)}
                  endPlaceHolder={getLocalizationData(localizationData, 'to_date_label_copy', 'To', device)}
                  startDateState={'startDate'}
                  endDateState={'endDate'}
                  singleMode={false}
                  doubleMonths={true}
                  selectCopy={getLocalizationData(localizationData, 'date_picker_select', 'Select Date', device)}
                  startLabel={getLocalizationData(localizationData, 'from_date_label_copy', 'Departs as early as', device)}
                  endLabel={getLocalizationData(localizationData, 'to_date_label_copy', 'Departs as late as', device)}
                  singleDateSelect={true}
                  dateFormat={searchdDateFormat}
                />

              </div>
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
          <div className="wizard-search-button-container">
            <PrimaryButton {...primaryButtonProps} />
          </div>
        </form>
      </div>
    </>
  );
};
